import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";

const steps = [
  { n: "01", t: "20-minute call", d: "We learn your practice size, systems and vendors." },
  { n: "02", t: "Assessment", d: "Questionnaire, review of your systems and policies, and a scan of your public exposure." },
  { n: "03", t: "Readout", d: "A written risk analysis, a prioritized fix list, and policy templates. Optional monthly support afterward." },
];

const gets = [
  "Written security risk analysis for your records",
  "Top risks ranked in plain English",
  "Fix list your IT provider can act on",
  "Policy and staff-training gaps identified",
  "Help answering cyber-insurance security questions",
];

export default function Hipaa() {
  return (
    <PageLayout
      seoTitle="HIPAA Security Risk Assessment for DFW Medical & Dental Practices | Aliwer"
      seoDescription="Fixed-price HIPAA security risk assessments for independent medical and dental practices in Dallas–Fort Worth. Written risk analysis, prioritized fix list, and ongoing support — local and plain-English."
      path="/hipaa"
      eyebrow="HIPAA security for DFW medical and dental practices"
      title="Pass your HIPAA security risk assessment without hiring an IT team."
      subtitle="Aliwer gives independent practices a fixed-price risk assessment, a clear fix list, and ongoing support. Local, plain-English, done in about 2 weeks."
      testid="hipaa-page"
    >
      {/* CTAs + trust line */}
      <Reveal>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <Link
            to="/baseline-review"
            data-testid="hipaa-baseline-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-8 py-4 hover:bg-white transition-colors"
          >
            Book a $499 Baseline Review
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/exposure-check"
            data-testid="hipaa-exposure-link"
            className="inline-flex items-center text-sm text-fog hover:text-paper underline underline-offset-4 decoration-white/30 transition-colors"
          >
            Get a free email and website exposure check
          </Link>
        </div>
        <p className="text-xs uppercase tracking-widest text-fog mb-16" data-testid="hipaa-trust-line">
          Founder-led · Southlake, TX · Business associate agreement (BAA) signed with every client
        </p>
      </Reveal>

      {/* Problem */}
      <Reveal>
        <div className="mb-16 max-w-3xl" data-testid="hipaa-problem">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-paper mb-4">
            The risk analysis is required. Most small practices don't have one.
          </h2>
          <p className="text-sm sm:text-base text-fog leading-relaxed">
            HIPAA requires a documented security risk analysis. Without one, a breach, an audit or a
            cyber-insurance application can turn into fines, denied claims, or days of downtime. We do the
            work for you and leave you with documents you can show.
          </p>
        </div>
      </Reveal>

      {/* How it works */}
      <Reveal>
        <div className="mb-16" data-testid="hipaa-steps">
          <p className="text-xs uppercase tracking-[0.3em] text-fog mb-5">How it works</p>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {steps.map((s) => (
              <div key={s.n} className="bg-surface p-7">
                <span className="font-display text-3xl tracking-tighter text-fog/50">{s.n}</span>
                <h3 className="mt-4 text-sm font-medium text-paper">{s.t}</h3>
                <p className="mt-2 text-xs text-fog leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* What you get + pricing strip */}
      <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] mb-16">
        <Reveal>
          <div data-testid="hipaa-what-you-get">
            <p className="text-xs uppercase tracking-[0.3em] text-fog mb-5">What you get</p>
            <ul className="space-y-3.5">
              {gets.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm sm:text-base text-paper/90 leading-relaxed">
                  <Check size={17} className="text-fog mt-0.5 shrink-0" />
                  {g}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="rounded-2xl border border-white/15 bg-surface p-7 self-start" data-testid="hipaa-pricing-strip">
            <p className="text-xs uppercase tracking-[0.3em] text-fog mb-5">Pricing</p>
            <div className="space-y-4">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-sm text-paper">Baseline Review</span>
                <span className="font-display text-xl tracking-tight text-paper">$499</span>
              </div>
              <div className="h-px bg-white/10" />
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-sm text-paper">Full HIPAA Risk Assessment</span>
                <span className="font-display text-xl tracking-tight text-paper">from $2,500</span>
              </div>
              <p className="text-xs text-fog">$2,500 for practices under 15 staff · up to $4,500 for larger practices</p>
              <div className="h-px bg-white/10" />
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-sm text-paper">Compliance Care plan</span>
                <span className="font-display text-xl tracking-tight text-paper">from $750<span className="text-xs text-fog font-sans"> /mo</span></span>
              </div>
              <p className="text-xs text-fog">Quarterly checks, policy updates, staff security training, annual assessment refresh, incident hotline.</p>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Proof */}
      <Reveal>
        <div className="mb-16 rounded-2xl border border-white/10 bg-black/40 p-7 flex items-start gap-4" data-testid="hipaa-proof">
          <ShieldCheck size={20} className="text-paper mt-0.5 shrink-0" strokeWidth={1.3} />
          <p className="text-sm text-fog leading-relaxed max-w-3xl">
            Founder-led by <span className="text-paper">Lorenzo Openito</span>, Aliwer is a Southlake, TX
            cybersecurity consultancy pursuing SOC 2 Type II with a{" "}
            <Link to="/trust/soc2-journey" className="underline underline-offset-2 text-paper hover:text-fog transition-colors">public compliance journey</Link>.
            Client testimonials and case studies are published here as engagements complete — with written
            client approval, never invented.
          </p>
        </div>
      </Reveal>

      {/* Final CTA */}
      <Reveal>
        <div className="rounded-2xl border border-white/15 bg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8" data-testid="hipaa-final-cta">
          <h2 className="flex-1 font-display text-2xl sm:text-3xl tracking-tight text-paper">
            Know where your practice stands in 2 weeks.
          </h2>
          <Link
            to="/baseline-review"
            data-testid="hipaa-final-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-8 py-4 hover:bg-white transition-colors shrink-0"
          >
            Book a $499 Baseline Review
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </Reveal>
    </PageLayout>
  );
}
